import { portalAPI } from 'store/api/portalAPI';
import { AiChatThreadDetail, AiChatThreadSummary } from 'types/aiChat';
import { MeetingPrepViewResponse } from 'types/meetingPrep';
import { QueryResponse } from 'types/paginationAndFilter';

export type UiContextError = 'UNSUPPORTED_CONTEXT' | 'CONTEXT_READ_FAILED';
export type UiContextResult = { result: unknown } | { error: UiContextError };

interface CompleteUiContextRequest {
    requestId: string;
    body: UiContextResult;
}

export const aiChatApi = portalAPI
    .enhanceEndpoints({ addTagTypes: ['AiChatThreads', 'AiChatThreadMessages'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getAiChatThreads: builder.query<AiChatThreadSummary[], number>({
                query: (page) => `/v1/ai/chat/threads?page=${page}&size=20&sort=lastMessageAt,desc`,
                serializeQueryArgs: ({ endpointName }) => {
                    return endpointName;
                },
                merge: (currentCache, newItems) => {
                    // Check for duplicates to be safe, though page-based fetch shouldn't overlap normally
                    const newUniqueItems = newItems.filter(
                        (newItem) => !currentCache.some((existing) => existing.id === newItem.id)
                    );
                    currentCache.push(...newUniqueItems);
                },
                forceRefetch({ currentArg, previousArg }) {
                    return currentArg !== previousArg;
                },
                transformResponse: (response: QueryResponse<AiChatThreadSummary>) => response.content,
                providesTags: ['AiChatThreads'],
            }),
            getAiChatThreadMessages: builder.query<AiChatThreadDetail, number>({
                query: (threadId) => `/v1/ai/chat/threads/${threadId}`,
                providesTags: (_result, _error, threadId) => [{ type: 'AiChatThreadMessages', id: threadId }],
            }),
            getMeetingPrepView: builder.query<MeetingPrepViewResponse, number>({
                query: (messageId) => `/v1/ai/meeting-prep/view/${messageId}`,
            }),
            deleteAiChatThread: builder.mutation<void, number>({
                query: (threadId) => ({
                    url: `/v1/ai/chat/threads/${threadId}`,
                    method: 'DELETE',
                }),
                // Optimistic update to remove thread from list
                async onQueryStarted(threadId, { dispatch, queryFulfilled }) {
                    const patchResult = dispatch(
                        aiChatApi.util.updateQueryData('getAiChatThreads', 0, (draft) => {
                            // Remove the deleted thread from the list
                            return draft.filter((t) => t.id !== threadId);
                        })
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patchResult.undo();
                    }
                },
            }),
            completeUiContextRequest: builder.mutation<void, CompleteUiContextRequest>({
                query: ({ requestId, body }) => ({
                    url: `/v1/ai/chat/ui-context-results/${encodeURIComponent(requestId)}`,
                    method: 'POST',
                    body,
                }),
                extraOptions: { skipButtonDisable: true },
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetAiChatThreadsQuery,
    useLazyGetAiChatThreadMessagesQuery,
    useDeleteAiChatThreadMutation,
    useCompleteUiContextRequestMutation,
    useGetMeetingPrepViewQuery,
} = aiChatApi;
